import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { auth } from 'firebase/firebase';

import { canGetOrganizationDocument, isPackedClaims } from 'packages/utils';

import {
  errorNotification,
  organizationFetch,
  organizationSelect,
  userAccessFetchError,
  userAccessFetchSuccess,
} from 'appRedux/actions';
import { USER_ACCESS_FETCH, USER_PROFILE_FETCH_SUCCESS } from 'constants/ActionTypes';
import { actions } from 'packages/divisions';

const userAccessFetchRequest = async () =>
  auth.currentUser.getIdTokenResult().then(result => result);

let activeOrganizationId = null;

const getClaims = state => state.user.access.data?.claims;
const getSelectedOrgId = state => state.organizations.organization.id;
const getProfileOrganizations = state => state.user.profile.data?.organizations;

/**
 * Picks and selects the initial organization from claims, localStorage, or (when packed)
 * the profile membership map. Safe to call again after profile loads: if an org is already
 * active it is a no-op.
 */
function* selectInitialOrganization(claims) {
  if (!claims) {
    return;
  }

  // Packed token (pk flag): the claims org map may be incomplete, so the profile's
  // membership map is the fallback source for the initial organization.
  const profileOrganizations = yield select(getProfileOrganizations);
  const packedFallbackOrgId = isPackedClaims(claims)
    ? Object.keys(profileOrganizations || {})?.[0]
    : undefined;

  // @todo load from local store the last organization selected in case of refresh
  const initialOrganizationId =
    localStorage.getItem('activeOrg') ||
    Object.keys(claims.org || {})?.[0] ||
    packedFallbackOrgId;

  const canGetOrganization = canGetOrganizationDocument(claims, initialOrganizationId);

  if (initialOrganizationId && activeOrganizationId !== initialOrganizationId) {
    yield put(organizationSelect(initialOrganizationId));
    yield put(actions.assignedDivisionsFetch(initialOrganizationId));
    if (canGetOrganization) {
      yield put(organizationFetch(initialOrganizationId));
    }
    activeOrganizationId = initialOrganizationId;
  }
}

function* userAccessFetch() {
  try {
    const response = yield call(userAccessFetchRequest);
    yield put(userAccessFetchSuccess(response));
    yield call(selectInitialOrganization, response.claims);
  } catch (error) {
    yield put(userAccessFetchError(error));
    yield put(errorNotification(error.toString()));
  }
}

/**
 * Profile and access are dispatched concurrently (token.js). When access wins the race for
 * a packed token, claims.org is empty and profile organizations are not in the store yet,
 * so no org is selected. Re-evaluate once the profile snapshot arrives.
 */
function* onProfileFetchSuccess() {
  const claims = yield select(getClaims);
  if (!isPackedClaims(claims)) {
    return;
  }

  const selectedOrgId = yield select(getSelectedOrgId);
  if (selectedOrgId || activeOrganizationId) {
    return;
  }

  yield call(selectInitialOrganization, claims);
}

export function* fetchUserAccess() {
  yield takeEvery(USER_ACCESS_FETCH, userAccessFetch);
}

export function* watchProfileForPackedOrgSelect() {
  yield takeEvery(USER_PROFILE_FETCH_SUCCESS, onProfileFetchSuccess);
}

export default function* rootSaga() {
  yield all([fork(fetchUserAccess), fork(watchProfileForPackedOrgSelect)]);
}
